import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Play, CheckCircle, Lock, Award, Trophy, Star } from 'lucide-react';

export default function SongProgressPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Song data
  const [song, setSong] = useState(null);
  const [musical, setMusical] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleAnalysis, setSelectedRoleAnalysis] = useState(null);
  
  // Progress data
  const [lessonProgress, setLessonProgress] = useState([]);
  const [quizAttempts, setQuizAttempts] = useState({});
  
  // Get song ID from params or search params
  const songId = id || searchParams?.get('songId');

  useEffect(() => {
    if (songId && user) {
      loadSongData();
    }
  }, [songId, user]);

  useEffect(() => {
    if (selectedRole && user) {
      loadRoleProgress();
    }
  }, [selectedRole, user]);

  const loadSongData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch song with musical info
      const { data: songData, error: songError } = await supabase?.from('songs')?.select(`
          *,
          musicals!inner (
            id,
            title,
            poster_url,
            description
          )
        `)?.eq('id', songId)?.single();

      if (songError) throw songError;
      if (!songData) throw new Error('Song not found');

      setSong(songData);
      setMusical(songData?.musicals);

      // Fetch roles associated with this song
      const { data: songRoles, error: rolesError } = await supabase?.from('song_roles')?.select(`
          role_id,
          roles!inner (
            id,
            character_name,
            description,
            voice_type,
            vocal_range,
            age_range
          )
        `)?.eq('song_id', songId);

      if (rolesError) throw rolesError;

      const rolesData = songRoles?.map(sr => sr?.roles) || [];
      setRoles(rolesData);

      // Auto-select first role if available
      if (rolesData?.length > 0) {
        setSelectedRole(rolesData?.[0]);
      }

    } catch (err) {
      console.error('Error loading song data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRoleProgress = async () => {
    try {
      // Fetch character analysis for selected role
      const { data: analysis, error: analysisError } = await supabase?.from('character_song_analyses')?.select('*')?.eq('song_id', songId)?.eq('role_id', selectedRole?.id)?.single();

      if (analysisError) {
        console.error('Error fetching analysis:', analysisError);
        setSelectedRoleAnalysis(null);
        setLessonProgress([]);
        return;
      }

      setSelectedRoleAnalysis(analysis);

      // Fetch user's lesson progress for this character analysis
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('*')?.eq('user_id', user?.id)?.eq('song_id', songId)?.eq('character_analysis_id', analysis?.id);

      if (progressError) throw progressError;

      setLessonProgress(progressData || []);

      // Fetch quiz attempts for mastery display
      const progressIds = progressData?.map(p => p?.id) || [];
      if (progressIds?.length > 0) {
        const { data: attempts, error: attemptsError } = await supabase?.from('lesson_quiz_attempts')?.select('*')?.eq('user_id', user?.id)?.in('quiz_id', progressIds)?.order('created_at', { ascending: false });

        if (!attemptsError && attempts) {
          const attemptsMap = {};
          attempts?.forEach(attempt => {
            if (!attemptsMap?.[attempt?.quiz_id]) {
              attemptsMap[attempt.quiz_id] = attempt;
            }
          });
          setQuizAttempts(attemptsMap);
        }
      }

    } catch (err) {
      console.error('Error loading role progress:', err);
      setError(err?.message);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleLessonClick = (lessonSection) => {
    if (!selectedRoleAnalysis) return;
    
    navigate(`/lesson-interface?songId=${songId}&roleId=${selectedRole?.id}&analysisId=${selectedRoleAnalysis?.id}&lesson=${lessonSection}`);
  };

  const getLessonProgress = (lessonSection) => {
    return lessonProgress?.find(p => p?.lesson_section === lessonSection);
  };

  const getQuadrantStatus = (lessonSection) => {
    const progress = getLessonProgress(lessonSection);
    if (!progress) return 'locked';
    
    if (progress?.is_quiz_perfect) return 'mastered'; // Gold
    if (progress?.lesson_status === 'completed') return 'completed'; // Purple
    if (progress?.lesson_status === 'started') return 'started'; // Blue
    if (progress?.lesson_status === 'available') return 'available'; // Blue outline
    
    return 'locked'; // Gray
  };

  const getProgressPercentage = () => {
    if (lessonProgress?.length === 0) return 0;
    
    const lessons = ['overview', 'level1', 'level2', 'level3', 'level4'];
    const completed = lessons?.filter(l => {
      const progress = getLessonProgress(l);
      return progress?.lesson_status === 'completed';
    })?.length;
    
    return Math.round((completed / lessons?.length) * 100);
  };

  const isLesson5Complete = () => {
    const level5Progress = getLessonProgress('level5');
    return level5Progress?.lesson_status === 'completed';
  };

  const renderProgressPieChart = () => {
    const lessons = [
      { section: 'level1', position: 'top-right', rotation: 45 },
      { section: 'level2', position: 'bottom-right', rotation: 135 },
      { section: 'level3', position: 'bottom-left', rotation: 225 },
      { section: 'level4', position: 'top-left', rotation: 315 }
    ];

    const getQuadrantColor = (status) => {
      switch (status) {
        case 'mastered': return '#FFD700'; // Gold
        case 'completed': return '#9333EA'; // Purple
        case 'started': return '#3B82F6'; // Blue
        case 'available': return 'transparent';
        default: return '#E5E7EB'; // Gray
      }
    };

    const getQuadrantBorder = (status) => {
      if (status === 'available') return '2px solid #3B82F6';
      return 'none';
    };

    return (
      <div className="relative w-32 h-32 mx-auto">
        {/* Pie chart quadrants */}
        <svg className="w-full h-full" viewBox="0 0 100 100">
          {/* Lesson 1 - Top Right */}
          <path
            d="M 50 50 L 50 0 A 50 50 0 0 1 100 50 Z"
            fill={getQuadrantColor(getQuadrantStatus('level1'))}
            stroke={getQuadrantBorder(getQuadrantStatus('level1'))}
            strokeWidth="2"
          />
          {/* Lesson 2 - Bottom Right */}
          <path
            d="M 50 50 L 100 50 A 50 50 0 0 1 50 100 Z"
            fill={getQuadrantColor(getQuadrantStatus('level2'))}
            stroke={getQuadrantBorder(getQuadrantStatus('level2'))}
            strokeWidth="2"
          />
          {/* Lesson 3 - Bottom Left */}
          <path
            d="M 50 50 L 50 100 A 50 50 0 0 1 0 50 Z"
            fill={getQuadrantColor(getQuadrantStatus('level3'))}
            stroke={getQuadrantBorder(getQuadrantStatus('level3'))}
            strokeWidth="2"
          />
          {/* Lesson 4 - Top Left */}
          <path
            d="M 50 50 L 0 50 A 50 50 0 0 1 50 0 Z"
            fill={getQuadrantColor(getQuadrantStatus('level4'))}
            stroke={getQuadrantBorder(getQuadrantStatus('level4'))}
            strokeWidth="2"
          />
          
          {/* Lesson 5 completion ring */}
          {isLesson5Complete() && (
            <circle
              cx="50"
              cy="50"
              r="48"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="4"
            />
          )}
        </svg>

        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {getProgressPercentage()}%
            </div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
        </div>
      </div>
    );
  };

  const renderLessonLink = (lessonSection, lessonNumber, lessonTitle) => {
    const progress = getLessonProgress(lessonSection);
    const status = getQuadrantStatus(lessonSection);
    
    const isLocked = status === 'locked';
    const isCompleted = progress?.lesson_status === 'completed';
    const isMastered = progress?.is_quiz_perfect;
    
    return (
      <button
        key={lessonSection}
        onClick={() => !isLocked && handleLessonClick(lessonSection)}
        disabled={isLocked}
        className={`
          flex items-center justify-between p-4 rounded-lg transition-all
          ${isLocked 
            ? 'bg-gray-100 cursor-not-allowed opacity-50' :'bg-white hover:bg-gray-50 cursor-pointer border border-gray-200 hover:border-blue-300'
          }
        `}
      >
        <div className="flex items-center gap-3">
          <div className={`
            flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
            ${isMastered ? 'bg-yellow-100' : isCompleted ? 'bg-purple-100' : 'bg-blue-100'}
          `}>
            {isLocked ? (
              <Lock className="w-5 h-5 text-gray-400" />
            ) : isMastered ? (
              <Trophy className="w-5 h-5 text-yellow-600" />
            ) : isCompleted ? (
              <CheckCircle className="w-5 h-5 text-purple-600" />
            ) : (
              <Play className="w-5 h-5 text-blue-600" />
            )}
          </div>
          
          <div className="text-left">
            <div className="font-semibold text-gray-900">
              Lesson {lessonNumber}: {lessonTitle}
            </div>
            {progress && (
              <div className="text-sm text-gray-500">
                {isMastered ? (
                  <span className="text-yellow-600 font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" /> Mastered (80%+)
                  </span>
                ) : isCompleted ? (
                  <span className="text-purple-600">Completed</span>
                ) : progress?.lesson_status === 'started' ? (
                  <span className="text-blue-600">In Progress</span>
                ) : (
                  <span className="text-gray-500">Available</span>
                )}
              </div>
            )}
          </div>
        </div>
        {progress?.quiz_completed && (
          <div className="flex items-center gap-2">
            <Award className={`w-5 h-5 ${isMastered ? 'text-yellow-500' : 'text-gray-400'}`} />
            {progress?.quiz_score !== undefined && (
              <span className={`text-sm font-medium ${isMastered ? 'text-yellow-600' : 'text-gray-600'}`}>
                {progress?.quiz_score}%
              </span>
            )}
          </div>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !song) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Song not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <div className="flex gap-8">
            {/* Musical Poster */}
            {musical?.poster_url && (
              <div className="flex-shrink-0">
                <img
                  src={musical?.poster_url}
                  alt={musical?.title}
                  className="w-48 h-64 object-cover rounded-lg shadow-md"
                />
              </div>
            )}
            
            {/* Song Info */}
            <div className="flex-1">
              <button
                onClick={() => navigate(-1)}
                className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
              >
                ← Back
              </button>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {song?.title}
              </h1>
              
              <p className="text-xl text-gray-600 mb-4">
                from {musical?.title}
              </p>
              
              {song?.overview?.notes && (
                <p className="text-gray-700 leading-relaxed">
                  {song?.overview?.notes}
                </p>
              )}
              
              {/* Song Details */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                {song?.act_number && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-500">Act</div>
                    <div className="font-semibold text-gray-900">Act {song?.act_number}</div>
                  </div>
                )}
                {song?.key_signature && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-500">Key</div>
                    <div className="font-semibold text-gray-900">{song?.key_signature}</div>
                  </div>
                )}
                {roles?.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-500">Characters</div>
                    <div className="font-semibold text-gray-900">{roles?.length} role{roles?.length !== 1 ? 's' : ''}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Roles Section */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Select a Character for Song Analysis
          </h2>
          
          {roles?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No roles available for this song yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles?.map((role) => (
                <button
                  key={role?.id}
                  onClick={() => handleRoleSelect(role)}
                  className={`
                    p-6 rounded-xl text-left transition-all
                    ${selectedRole?.id === role?.id
                      ? 'bg-blue-50 border-2 border-blue-500 shadow-md'
                      : 'bg-gray-50 border-2 border-transparent hover:border-blue-300 hover:shadow-sm'
                    }
                  `}
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {role?.character_name}
                  </h3>
                  
                  {role?.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {role?.description}
                    </p>
                  )}
                  
                  <div className="space-y-1">
                    {role?.voice_type && (
                      <div className="text-sm">
                        <span className="text-gray-500">Voice:</span>{' '}
                        <span className="font-medium text-gray-900">
                          {role?.voice_type?.replace('_', ' ')}
                        </span>
                      </div>
                    )}
                    {role?.vocal_range && (
                      <div className="text-sm">
                        <span className="text-gray-500">Range:</span>{' '}
                        <span className="font-medium text-gray-900">
                          {role?.vocal_range}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lessons Section - Only show if role is selected */}
        {selectedRole && selectedRoleAnalysis && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Learning Path for {selectedRole?.character_name}
              </h2>
              
              {/* Progress Pie Chart */}
              {renderProgressPieChart()}
            </div>

            {/* Lesson Links */}
            <div className="space-y-3">
              {renderLessonLink('overview', 0, 'Overview')}
              {renderLessonLink('level1', 1, 'Interpretation')}
              {renderLessonLink('level2', 2, 'Context & Emotion')}
              {renderLessonLink('level3', 3, 'Phrasing & Technique')}
              {renderLessonLink('level4', 4, 'Performance Tips')}
              {renderLessonLink('level5', 5, 'Mastery')}
            </div>

            {/* Progress Legend */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Progress Guide:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-gray-700">Available/Started</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                  <span className="text-sm text-gray-700">Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-gray-700">Mastered (80%+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-4 border-blue-500 bg-transparent"></div>
                  <span className="text-sm text-gray-700">Lesson 5 Ring</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedRole && !selectedRoleAnalysis && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">
                Song analysis for {selectedRole?.character_name} is not available yet.
              </p>
              <p className="text-sm text-gray-500">
                Please check back later or select a different character.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}